import { useEffect, useRef, useState } from 'react';

/**
 * `useMeasuredWidth()` → `[ref, width]` — attach `ref` to an element and get
 * its live `clientWidth` in CSS pixels, or `null` before the first measurement.
 *
 * ── What this is FOR, so it is not reached for casually ───────────────
 * Sizing a `position: sticky` panel that lives inside a horizontally
 * scrollable table. Such a panel must be **at most as wide as the scrollport**:
 * it is pinned to the scrollport's left edge, so anything past the right edge
 * moves away from the viewport as you scroll and can never be reached. Making
 * it any narrower than the scrollport, on the other hand, leaves dead
 * whitespace beside the row it belongs to.
 *
 * ── Why a measurement and not a CSS expression ────────────────────────
 * The obvious `w-[min(72rem,calc(100vw_-_2rem))]` is wrong in both directions
 * and each surface that tried it hit the same wall:
 *   - `100vw` is the VIEWPORT, and the app's content area is the viewport minus
 *     the page padding minus the sidebar — which is `lg:w-16` when collapsed and
 *     `lg:w-64` when not (`Layout.js`). So the expression over-estimates by up
 *     to 16rem and the panel is clipped unreachably.
 *   - the `72rem` cap then under-estimates on any wide screen, which is the
 *     "the chart lost most of the page" symptom `GagGroupTable` recorded and the
 *     "table view should be full width like cards view" one the order table did.
 * A ResizeObserver on the element that IS the content area answers both exactly
 * and needs to know nothing about the layout around it.
 *
 * ── The two details that are load-bearing ─────────────────────────────
 * 1. **`Math.round`, to stop a measurement loop.** The observer fires →
 *    `setState` → re-render → re-measure, and that second measurement must
 *    produce the SAME value so React bails on the no-op set. A fractional
 *    width can oscillate between two neighbouring values forever.
 * 2. **ResizeObserver, not just `window.resize`.** The content area changes
 *    width WITHOUT a window resize every time the sidebar is collapsed or
 *    expanded (a ~12rem swing), and a window listener alone would leave the
 *    panel mis-sized until the next actual resize. The `resize` listener is
 *    kept only as a fallback for environments with no ResizeObserver — jsdom
 *    has none by default, so tests take that path.
 *
 * `null` until the first measurement lands, so a caller can render a CSS
 * fallback for the first paint rather than a zero-width panel. Callers own any
 * adjustment (e.g. subtracting a card's borders) — this returns the raw
 * `clientWidth` and nothing else.
 *
 * Extracted 2026-08-08 from `components/gags/GagGroupTable.js`, which had the
 * only copy, when `components/OrderTableView.js` needed the same thing for the
 * same reason. Per CLAUDE.md's Code Unification Principle: the second use is
 * where it becomes shared, not the third.
 */
export default function useMeasuredWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(null);
  /* 🐛 FIXED 2026-10-25 — A LATE-MOUNTING ELEMENT WAS NEVER OBSERVED. The
     observer was attached in a mount-only effect, so an element rendered AFTER
     the hook's first effect (every Manage Budgets table mounts after its
     loading spinner) had no ResizeObserver and stayed `null` until a WINDOW
     resize — measured in the real build: the Cost Codes table's "Fit columns"
     and sticky-panel width were running unmeasured on load. `ref.current` is
     not reactive, so the element is promoted into STATE by an effect with NO
     dependency array — the `hooks/useDragScroll.js` fix for the identical trap
     (`hooks/CLAUDE.md`); do not give that effect one. A caller whose element
     exists at mount is unchanged but for one extra no-op render. */
  const [el, setEl] = useState(null);
  useEffect(() => {
    if (ref.current !== el) setEl(ref.current);
  });

  useEffect(() => {
    const measure = () => {
      if (ref.current) setWidth(Math.round(ref.current.clientWidth));
    };
    measure();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [el]);

  return [ref, width];
}
